/* eslint-disable no-shadow, react/prop-types, quote-props, react/jsx-no-bind */
import classnames from 'classnames';
import React, { Component, PropTypes } from 'react';
import Keys, { KeyHelpers } from 'es6/core/keys';
import Glyph, { CHEVRON_LEFT, CHEVRON_RIGHT } from 'es6/components/common/glyph';
import MouseoverDictionary from 'iknow-mouseover-dictionary/react';
import FitText from 'es6/components/common/fit-text';
import SoundPlayer from 'es6/components/common/sound-player';
import SoundManager from 'es6/services/sound-manager';
import { t } from 'es6/services/localization';
import { TranscodedImageSizes, TranscodedImage } from 'es6/components/common/transcoded-image';

// TODO: See if there is a good way to decouple this.
const SENTENCE_LIST_WIDTH = 720;

/**
 * @this SentenceTab
 * @param {Number} shiftBy    how many sentences to shift over by (use negative to go back)
 */
function adjustSentenceIndex(shiftBy) {
  const { sentences } = this.props;
  const { sentenceIndex } = this.state;
  let newIndex = sentenceIndex + shiftBy;

  // Make sure we aren't outside possible ranges.
  if (newIndex < 0) {
    newIndex = 0;
  } else if (newIndex >= sentences.length) {
    newIndex = sentences.length - 1;
  }

  // If the index is new, update the state. Stop the sound of that sentence
  // if it is playing.
  if (newIndex !== sentenceIndex) {
    stopSound.call(this);
    this.setState({ sentenceIndex: newIndex });
  }
}

/**
 * @this SentenceTab
 */
function handleKeydown(e) {
  const { keyCode } = e;
  const { disableVimKeys } = this.props;
  const { isOpen } = this.state;

  const { isUp, isDown, isLeft, isRight } = disableVimKeys ? {
    isUp: keyCode => keyCode === Keys.UP,
    isDown: keyCode => keyCode === Keys.DOWN,
    isLeft: keyCode => keyCode === Keys.LEFT,
    isRight: keyCode => keyCode === Keys.RIGHT,
  } : KeyHelpers;

  // Detect actions for the user being in sentence mode.
  if ((isUp(keyCode) && !isOpen) || (isDown(keyCode) && isOpen)) {
    toggleSentenceTab.call(this);
  } else if (isOpen) {
    if (isLeft(keyCode)) {
      adjustSentenceIndex.call(this, -1);
    } else if (isRight(keyCode)) {
      adjustSentenceIndex.call(this, 1);
    } else if (keyCode === Keys.SPACE) {
      playSound.call(this);
    }
  }
}

/**
 * Trigger onOpened and onClosed events.
 *
 * @this SentenceTab
 */
function handleTransitionEnd() {
  if (this.state.isOpen) {
    playSound.call(this);
  }
}

/**
 * @this SentenceTab
 */
function playSound() {
  const { sentenceIndex } = this.state;
  const { sentences } = this.props;
  SoundManager.play(sentences[sentenceIndex].sound);
}

function Sentence({ sentence }) {
  const image = sentence.getImage();

  return (
    <li className={classnames('sentence', { 'has_image': !!image })}>
      {image && <TranscodedImage src={image} size={TranscodedImageSizes.APP_SENTENCE} className="image" />}

      <div className="content">
        <div className="top">
          <div className="bottom_aligned">
            <span className="text-and-transliteration">
              <FitText key={sentence.getText() + sentence.getTransliteration()}>
                <p>
                  <span className="text" lang={sentence.cue.language}>
                    <MouseoverDictionary html={sentence.getText()} />
                  </span>
                  {' '}
                  {/* Don't shrink the font-size of the sound player icon. */}
                  {sentence.sound &&
                    <span data-fit-text="no-resize">
                      <SoundPlayer source={sentence.sound} data-fit-text="no-resize" />
                    </span>}
                </p>
                {sentence.getTransliteration() &&
                  <p className="transliteration">
                    <span dangerouslySetInnerHTML={{ __html: sentence.getTransliteration() }} />
                  </p>}
              </FitText>
            </span>
          </div>
        </div>

        <div className="hr"></div>

        <p className="translation">
          <FitText><span dangerouslySetInnerHTML={{ __html: sentence.getTranslation() }} /></FitText>
        </p>
      </div>
    </li>
  );
}

/**
 * @this SentenceTab
 */
function stopSound() {
  const { sentenceIndex } = this.state;
  const { sentences } = this.props;
  SoundManager.stop(sentences[sentenceIndex].sound);
}

/**
 * @this SentenceTab
 */
function toggleSentenceTab() {
  const { onOpen, onClose } = this.props;
  const { isOpen } = this.state;

  if (isOpen) {
    onClose.call();
    stopSound.call(this);
    this.setState({ isOpen: false });
  } else {
    onOpen.call();
    this.setState({ isOpen: true });
  }
}

const INITIAL_STATE = { sentenceIndex: 0, isOpen: false };

class SentenceTab extends Component {
  constructor(props) {
    super(props);
    this.handleKeydown = handleKeydown.bind(this);
    this.state = INITIAL_STATE;
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeydown);
  }

  componentWillUpdate(nextProps) {
    // If the sentences change, reset the state of the component.
    if (nextProps.sentences !== this.props.sentences) {
      this.setState(INITIAL_STATE);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  }

  render() {
    const { isOpen, sentenceIndex } = this.state;
    const { sentences } = this.props;

    return (
      <div className={classnames('sentence-tab', { 'sentence-tab-open': isOpen })} onTransitionEnd={handleTransitionEnd.bind(this)}>
        <h4 className="tab" onClick={toggleSentenceTab.bind(this)}>
          <span>{t('sentences')}</span>
        </h4>

        <div className="scrolling-list">
          <ul className="sentence-list" style={{ transform: `translateX(-${sentenceIndex * SENTENCE_LIST_WIDTH}px)` }}>
            {sentences.map((sentence) => <Sentence sentence={sentence} key={sentence.getId()} />)}
          </ul>
        </div>

        <div className="footer">
          <Glyph type={CHEVRON_LEFT} className="prev" onClick={adjustSentenceIndex.bind(this, -1)} />
          <Glyph type={CHEVRON_RIGHT} className="next" onClick={adjustSentenceIndex.bind(this, 1)} />

          <div className="pages">
            <span className="current">{sentenceIndex + 1}</span>
            {' '}
            <span className="separator">/</span>
            {' '}
            <span className="total">{sentences.length}</span>
          </div>
        </div>
      </div>
    );
  }
}

SentenceTab.propTypes = {
  disableVimKeys: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  sentences: PropTypes.arrayOf(PropTypes.object).isRequired,
};

SentenceTab.defaultProps = {
  disableVimKeys: false,
  onOpen: () => {},
  onClose: () => {},
};

export default SentenceTab;
